import React, { useRef, useState } from 'react';

/**
 * Tiny shared ripple helper for the interactive mood worlds. A tap spawns a soft
 * expanding ring at the point touched; rings clean themselves up after the
 * animation. Bounded (rings are short-lived) so taps never pile up DOM nodes.
 */
export interface Ripple {
  id: number;
  x: number;
  y: number;
}

export function useRipples() {
  const seq = useRef(0);
  const [ripples, setRipples] = useState<Ripple[]>([]);

  const spawnAt = (host: HTMLElement | null, clientX: number, clientY: number) => {
    if (!host) return;
    const hr = host.getBoundingClientRect();
    const id = seq.current++;
    setRipples((p) => [...p.slice(-6), { id, x: clientX - hr.left, y: clientY - hr.top }]);
    window.setTimeout(() => setRipples((p) => p.filter((r) => r.id !== id)), 700);
  };

  /** Spawn centred on an element (keyboard activation has no pointer coords). */
  const spawnAtElement = (el: HTMLElement) => {
    const host = el.closest('.mm-world') as HTMLElement | null;
    const r = el.getBoundingClientRect();
    spawnAt(host, r.left + r.width / 2, r.top + r.height / 2);
  };

  return { ripples, spawnAt, spawnAtElement };
}

export const RippleLayer: React.FC<{ ripples: Ripple[] }> = ({ ripples }) => (
  <div className="mm-world__ripples" aria-hidden="true">
    {ripples.map((r) => (
      <span key={r.id} className="mm-world__ripple" style={{ left: r.x, top: r.y }} />
    ))}
  </div>
);
